import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from "axios"
import { getApiUrl } from "@/lib/utils"
import { getAccessToken } from "@/lib/token-getter"
import { getSignalRConnectionId } from "@/lib/signalr-connection-id"
import { sessionExpirationEmitter } from "@/lib/session-expiration-emitter"

export class ApiClient {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: getApiUrl(),
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor for auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        const signalRConnectionId = getSignalRConnectionId()
        if (signalRConnectionId) {
          config.headers["X-SignalR-Connection-Id"] = signalRConnectionId
        }
        if (import.meta.env.DEV) {
          console.log("📤 API Request:", config.method?.toUpperCase(), config.url, {
            hasSignalRHeader: !!signalRConnectionId,
            signalRConnectionId,
          })
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          sessionExpirationEmitter.emit()
          console.warn("Session expired: Unauthorized (401)")
        } else if (error.response) {
          console.error(
            "API Error:",
            error.response.status,
            error.response.data
          )
        } else if (error.request) {
          console.error("Network Error:", error.message)
        } else {
          console.error("Request Error:", error.message)
        }
        return Promise.reject(error)
      }
    )
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  async downloadFile(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.client.get(url, {
      ...config,
      responseType: "blob",
      timeout: 600000, // 10 minutes for PDF generation
    });
    return response.data;
  }

  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.client.get(url, {
      ...config,
      responseType: "blob",
    });
    return response.data;
  }
}

export const apiClient = new ApiClient();
